import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './AddBuilding.module.css';
import { Mail, Lock, User, Phone, Home, Plus, Shield, ArrowLeft, Eye, EyeOff, Building } from 'lucide-react';
import { addBuildingForUser } from '../../Fetch/fetchBuilding';
import { fetchCity, fetchStreets, searchStreets } from '../../Fetch/fetchAddress';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';

export default function AddBuilding() {
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        city: '',
        address: '',
        addressNumber: '',
        numFloors: '',
        numApartments: '',
    });

    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');


    // City autocomplete state
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [showCitySuggestions, setShowCitySuggestions] = useState(false);
    const [newCityMode, setNewCityMode] = useState(false);
    const citySuggestionsRef = useRef(null);
    const [isCitySearching, setIsCitySearching] = useState(false);

    // Address autocomplete state
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
    const [newAddressMode, setNewAddressMode] = useState(false);
    const [isAddressSearching, setIsAddressSearching] = useState(false);
    const addressSuggestionsRef = useRef(null);

    // Store data
    const [israeliCities, setIsraeliCities] = useState([]);
    const [popularStreets, setPopularStreets] = useState([]);

    // Get user data from localStorage
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            setUserData(user);
        } else {
            setError('משתמש לא מחובר');
            navigate('/login');
        }
    }, [navigate]);

    // Simple error handling function
    const logError = (message, error) => {
        console.log(message, error);
    };

    // Debounced search function for streets
    const debouncedSearchStreets = useCallback(
        _.debounce(async (searchQuery) => {
            if (searchQuery?.length < 2) {
                setAddressSuggestions([]);
                setShowAddressSuggestions(false);
                setIsAddressSearching(false);
                return;
            }

            setIsAddressSearching(true);
            try {
                const results = await searchStreets(searchQuery);
                setAddressSuggestions(Array.isArray(results) ? results : []);
                setShowAddressSuggestions(true);
            } catch (error) {
                // Fallback to local filtering if API fails
                const filtered = popularStreets.filter(street =>
                    typeof street === 'string' &&
                    street.toLowerCase().includes(searchQuery.toLowerCase())
                );
                setAddressSuggestions(filtered);
                setShowAddressSuggestions(filtered?.length > 0);
            } finally {
                setIsAddressSearching(false);
            }
        }, 300),
        [popularStreets]
    );

    // Load cities and streets on component mount
    useEffect(() => {
        const loadCitiesAndStreets = async () => {
            try {
                // Fetch cities
                const citiesResponse = await fetchCity();

                if (Array.isArray(citiesResponse)) {
                    const cityNames = [];
                    citiesResponse.forEach(city => {
                        if (typeof city === 'string') {
                            cityNames.push(city);
                        } else if (city && typeof city === 'object') {
                            const name = city.name || city.cityName;
                            if (name) cityNames.push(name);
                        }
                    });
                    setIsraeliCities(cityNames);
                } else {
                    setIsraeliCities(['תל אביב', 'ירושלים', 'חיפה', 'באר שבע', 'ראשון לציון']);
                }

                // Fetch streets
                const streetsResponse = await fetchStreets();

                if (Array.isArray(streetsResponse)) {
                    const streetNames = [];
                    streetsResponse.forEach(street => {
                        if (typeof street === 'string') {
                            streetNames.push(street);
                        } else if (street && typeof street === 'object') {
                            const name = street.name || street.streetName;
                            if (name) streetNames.push(name);
                        }
                    });
                    setPopularStreets(streetNames);
                } else {
                    setPopularStreets(['הרצל', 'ביאליק', 'ויצמן', 'אלנבי', 'בן גוריון']);
                }
            } catch (error) {
                logError('Error loading initial data:', error);
                setIsraeliCities(['תל אביב', 'ירושלים', 'חיפה', 'באר שבע', 'ראשון לציון']);
                setPopularStreets(['הרצל', 'ביאליק', 'ויצמן', 'אלנבי', 'בן גוריון']);
            }
        };

        loadCitiesAndStreets();
    }, []);

    // Handle click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (citySuggestionsRef.current && !citySuggestionsRef.current.contains(event.target)) {
                setShowCitySuggestions(false);
            }
            if (addressSuggestionsRef.current && !addressSuggestionsRef.current.contains(event.target)) {
                setShowAddressSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));

        // City autocomplete
        if (name === 'city' && !newCityMode) {
            if (value.trim()) {
                const filtered = israeliCities.filter(city =>
                    typeof city === 'string' && city.toLowerCase().includes(value.toLowerCase())
                );
                setCitySuggestions(filtered);
                setShowCitySuggestions(filtered?.length > 0);
            } else {
                setCitySuggestions([]);
                setShowCitySuggestions(false);
            }
        }

        // Address autocomplete
        if (name === 'address' && !newAddressMode) {
            if (value.trim()) {
                debouncedSearchStreets(value.trim());
            } else {
                setAddressSuggestions([]);
                setShowAddressSuggestions(false);
            }
        }
    };

    const handleSuggestionClick = (name, value) => {
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));

        if (name === 'city') {
            setShowCitySuggestions(false);
        } else if (name === 'address') {
            setShowAddressSuggestions(false);
        }
    };

    const handleAddNew = (type) => {
        if (type === 'city') {
            setNewCityMode(true);
            setShowCitySuggestions(false);
        } else if (type === 'address') {
            setNewAddressMode(true);
            setShowAddressSuggestions(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        // Validate form fields
        if (!formData.city || !formData.address || !formData.addressNumber || !formData.numFloors || !formData.numApartments) {
            setError('אנא מלא את כל השדות');
            setIsLoading(false);
            return;
        }

        // Validate numbers
        if (isNaN(formData.numFloors) || parseInt(formData.numFloors) <= 0) {
            setError('מספר קומות לא תקין');
            setIsLoading(false);
            return;
        }

        if (isNaN(formData.numApartments) || parseInt(formData.numApartments) <= 0) {
            setError('מספר דירות לא תקין');
            setIsLoading(false);
            return;
        }

        try {
            if (!userData) {
                setError('משתמש לא מחובר');
                setIsLoading(false);
                return;
            }

            // Prepare building data for the new endpoint
            const buildingData = {
                city: formData.city,
                address: formData.address,
                addressNumber: formData.addressNumber,
                numApartments: parseInt(formData.numApartments),
                numFloors: parseInt(formData.numFloors)
            };

            // Call the new building API endpoint
            const result = await addBuildingForUser(buildingData);

            // Set success message
            setSuccessMessage(result.message || 'הבניין נוסף בהצלחה!');
            
            // Dispatch event to notify Layout to refresh building list
            window.dispatchEvent(new CustomEvent('buildingAdded', {
                detail: { newBuilding: result.building }
            }));
            
            // Navigate back to dashboard after a moment
            setTimeout(() => {
                navigate('/app/dashboard');
            }, 2000);

        } catch (error) {
            setError(error.message || 'שגיאה בהוספת הבניין. אנא נסה שוב.');
        } finally {
            setIsLoading(false);
        }
    };


    const handleBackClick = () => {
        navigate(-1);
    };


    return (
        <div className={styles.addBuildingContainer} dir="rtl">
            <div className={styles.backButtonContainer}>
                <button
                    onClick={handleBackClick}
                    className={styles.backButton}
                    type="button"
                >
                    <ArrowLeft size={20} />
                    חזרה
                </button>
            </div>

            <div className={styles.addBuildingCardWrapper}>
                <div className={styles.addBuildingCard}>
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className={styles.successMessage}>
                            {successMessage}
                        </div>
                    )}

                    <div className={styles.formContainer}>
                        <div className={styles.header}>
                            <div className={styles.logoContainer}>
                                <Building className={styles.logoIcon} />
                            </div>
                            <h1 className={styles.appTitle}>הוספת בניין חדש</h1>
                            <p className={styles.appSubtitle}>אנא מלא את פרטי הבניין החדש</p>
                            <p className={styles.trialText}>30 ימי ניסיון</p>
                        </div>

                        <form onSubmit={handleSubmit} autoComplete="off">
                            <div className={styles.formGroup}>
                                <label htmlFor="city" className={styles.formLabel}>עיר</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        className={styles.formInput}
                                        placeholder="הזן עיר"
                                        value={formData.city}
                                        onChange={handleChange}
                                        onFocus={() => {
                                            if (formData.city.trim() && !newCityMode) {
                                                const filtered = israeliCities.filter(city =>
                                                    typeof city === 'string' &&
                                                    city.toLowerCase().includes(formData.city.toLowerCase())
                                                );
                                                setCitySuggestions(filtered);
                                                setShowCitySuggestions(filtered?.length > 0);
                                            }
                                        }}
                                        required
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Home className={styles.inputIcon} />
                                    </div>
                                </div>

                                {showCitySuggestions && !newCityMode && (
                                    <div
                                        ref={citySuggestionsRef}
                                        className={styles.suggestionsContainer}
                                    >
                                        <div className={styles.dropdownList}>
                                            {citySuggestions?.length > 0 ? (
                                                citySuggestions?.map((city, index) => (
                                                    <div
                                                        key={index}
                                                        className={styles.dropdownItem}
                                                        onClick={() => handleSuggestionClick('city', city)}
                                                    >
                                                        {city}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className={styles.dropdownEmpty}>לא נמצאו תוצאות</div>
                                            )}
                                        </div>

                                        <div className={styles.dropdownAction}>
                                            <div className={styles.actionButtons}>
                                                <button
                                                    type="button"
                                                    className={styles.addNewButton}
                                                    onClick={() => handleAddNew('city')}
                                                >
                                                    <span>
                                                        <Plus size={16} />
                                                        הוסף עיר חדשה
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.cancelButton}
                                                    onClick={() => setShowCitySuggestions(false)}
                                                >
                                                    סגור
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="address" className={styles.formLabel}>רחוב</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        id="address"
                                        name="address"
                                        className={styles.formInput}
                                        placeholder="הזן רחוב"
                                        value={formData.address}
                                        onChange={handleChange}
                                        onFocus={() => {
                                            if (formData.address.trim() && !newAddressMode) {
                                                debouncedSearchStreets(formData.address.trim());
                                            }
                                        }}
                                        required
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Home className={styles.inputIcon} />
                                    </div>
                                </div>

                                {showAddressSuggestions && !newAddressMode && (
                                    <div
                                        ref={addressSuggestionsRef}
                                        className={styles.suggestionsContainer}
                                    >
                                        <div className={styles.dropdownList}>
                                            {isAddressSearching ? (
                                                <div className={styles.dropdownLoading}>
                                                    מחפש...
                                                </div>
                                            ) : addressSuggestions?.length > 0 ? (
                                                addressSuggestions?.map((street, index) => (
                                                    <div
                                                        key={index}
                                                        className={styles.dropdownItem}
                                                        onClick={() => handleSuggestionClick('address', street)}
                                                    >
                                                        {street}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className={styles.dropdownEmpty}>לא נמצאו תוצאות</div>
                                            )}
                                        </div>

                                        <div className={styles.dropdownAction}>
                                            <div className={styles.actionButtons}>
                                                <button
                                                    type="button"
                                                    className={styles.addNewButton}
                                                    onClick={() => handleAddNew('address')}
                                                >
                                                    <span>
                                                        <Plus size={16} />
                                                        הוסף רחוב חדש
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.cancelButton}
                                                    onClick={() => setShowAddressSuggestions(false)}
                                                >
                                                    סגור
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="addressNumber" className={styles.formLabel}>מספר בית</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="text"
                                            id="addressNumber"
                                            name="addressNumber"
                                            className={styles.formInput}
                                            placeholder="הזן מספר"
                                            value={formData.addressNumber}
                                            onChange={handleChange}
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Home className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="numFloors" className={styles.formLabel}>מספר קומות</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="number"
                                            id="numFloors"
                                            name="numFloors"
                                            className={styles.formInput}
                                            placeholder="הזן מספר"
                                            value={formData.numFloors}
                                            onChange={handleChange}
                                            min="1"
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Home className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="numApartments" className={styles.formLabel}>מספר דירות</label>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="number"
                                            id="numApartments"
                                            name="numApartments"
                                            className={styles.formInput}
                                            placeholder="הזן מספר"
                                            value={formData.numApartments}
                                            onChange={handleChange}
                                            min="1"
                                            required
                                        />
                                        <div className={styles.inputIconWrapper}>
                                            <Home className={styles.inputIcon} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={`${styles.submitButton} ${isLoading ? styles.disabled : ''}`}
                                disabled={isLoading}
                            >
                                {isLoading ? 'מוסיף בניין...' : 'הוסף בניין'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}